import { useEffect, useRef } from "react";
import { activityApi } from "../services/api/activityApi";

/**
 * POST VIEW TRACKING HOOK
 * Tracks when user views a post and how long they read it
 *
 * @param {string} postId - Post ID to track
 * @param {boolean} isVisible - Whether post is currently visible in viewport
 * @param {Object} options - Tracking options
 * @param {number} options.minReadTime - Minimum seconds to count as "read" (default: 3)
 * @param {boolean} options.enabled - Enable/disable tracking (default: true)
 */
export const usePostViewTracking = (postId, isVisible, options = {}) => {
  const viewTrackedRef = useRef(false);
  const startTimeRef = useRef(null);
  const readingTimerRef = useRef(null);
  const minReadTime = options.minReadTime || 3; // 3 seconds minimum
  const enabled = options.enabled !== false; // Default to enabled

  useEffect(() => {
    if (!enabled || !postId) return;

    if (isVisible) {
      // Track view (once per post)
      if (!viewTrackedRef.current) {
        activityApi.trackView(postId).catch(() => {
          // Silent fail for analytics
        });
        viewTrackedRef.current = true;
      }

      // Start reading time tracker
      startTimeRef.current = Date.now();
    } else {
      // Post left viewport - calculate reading time
      if (startTimeRef.current) {
        const readingTime = Math.floor(
          (Date.now() - startTimeRef.current) / 1000
        );

        // Only track if user read for at least minReadTime seconds
        if (readingTime >= minReadTime) {
          activityApi.trackReadingTime(postId, readingTime).catch(() => {
            // Silent fail for analytics
          });
        } else if (readingTime < 1 && viewTrackedRef.current) {
          // If viewed but scrolled past quickly (< 1 second), track as skip
          activityApi.trackSkip(postId).catch(() => {
            // Silent fail for analytics
          });
        }

        startTimeRef.current = null;
      }
    }

    // Cleanup on unmount
    return () => {
      if (startTimeRef.current) {
        const readingTime = Math.floor(
          (Date.now() - startTimeRef.current) / 1000
        );
        if (readingTime >= minReadTime) {
          activityApi.trackReadingTime(postId, readingTime).catch(() => {
            // Silent fail for analytics
          });
        }
      }
      if (readingTimerRef.current) {
        clearTimeout(readingTimerRef.current);
      }
    };
  }, [isVisible, postId, minReadTime, enabled]);

  return {
    viewTracked: viewTrackedRef.current,
  };
};

/**
 * BATCH POST TRACKING HOOK (For Performance)
 * Batches multiple activity tracking calls into single API request
 *
 * @returns {Object} { queueActivity, flushQueue }
 */
export const useBatchPostTracking = () => {
  const activityQueueRef = useRef([]);
  const flushTimerRef = useRef(null);

  const queueActivity = (activity) => {
    activityQueueRef.current.push(activity);

    // Flush after 5 seconds or when queue reaches 10 items
    if (activityQueueRef.current.length >= 10) {
      flushQueue();
    } else if (!flushTimerRef.current) {
      flushTimerRef.current = setTimeout(flushQueue, 5000);
    }
  };

  const flushQueue = async () => {
    if (activityQueueRef.current.length === 0) return;

    const activities = [...activityQueueRef.current];
    activityQueueRef.current = [];

    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }

    try {
      await activityApi.batchTrack(activities);
    } catch (error) {
      // Silent fail for analytics batch
    }
  };

  // Flush on unmount
  useEffect(() => {
    return () => {
      flushQueue();
    };
  }, []);

  return { queueActivity, flushQueue };
};

export default usePostViewTracking;
